/**
 * Réglages de société complémentaires (document 09 § 11).
 *
 * Trois familles, toutes rangées dans `tenants.settings` (objet JSON fusionné partiellement) :
 *   - `company`  : identité opérationnelle — assurance, profils d'activité métier, logo et
 *                  référentiels CRM (civilités, sources) ;
 *   - `email`    : signature appliquée aux e-mails sortants (logo société + expéditeur) ;
 *   - `calendar` : visibilité des agendas de l'équipe (toute l'équipe ou profils autorisés) et
 *     horaires de travail (plages d'ouverture de la journée).
 *
 * Ces réglages n'ont aucun effet de bord ici : les règles métier qui les consomment
 * (devis, e-mails, agenda) les résolvent à leur niveau.
 */
import { MAIL_TEMPLATE_KINDS, } from './mail-template.constants.js';
// ---------------------------------------------------------------------------------------------
// Profils d'activité métier
// ---------------------------------------------------------------------------------------------
/**
 * Activités spécifiques d'une société. Les profils retenus conditionneront plus tard
 * des paramètres métier dédiés (mentions obligatoires, garanties, barèmes).
 */
export const COMPANY_ACTIVITY_PROFILES = [
    'building',
    'public_works',
    'rge',
    'electricity',
    'plumbing',
    'carpentry',
    'roofing',
    'masonry',
    'painting',
    'landscaping',
    'architecture',
    'engineering',
];
/** Libellés des profils d'activité, présentés dans l'écran des paramètres société. */
export const COMPANY_ACTIVITY_PROFILE_LABELS = {
    building: 'Entreprise du bâtiment',
    public_works: 'Travaux publics',
    rge: "RGE (Reconnu Garant de l'Environnement)",
    electricity: 'Électricité',
    plumbing: 'Plomberie / chauffage',
    carpentry: 'Menuiserie',
    roofing: 'Couverture / toiture',
    masonry: 'Maçonnerie',
    painting: 'Peinture / finitions',
    landscaping: 'Paysage / espaces verts',
    architecture: "Architecture / maîtrise d'œuvre",
    engineering: "Bureau d'études / ingénierie",
};
/** Indique si une valeur inconnue est un profil d'activité valide. */
export function isCompanyActivityProfile(value) {
    return COMPANY_ACTIVITY_PROFILES.includes(value);
}
// ---------------------------------------------------------------------------------------------
// Clés des paramètres avancés (`tenants.settings`)
// ---------------------------------------------------------------------------------------------
/** Réglages d'identité opérationnelle de la société. */
export const COMPANY_SETTINGS_KEY = 'company';
/** Réglages des e-mails sortants (signature). */
export const EMAIL_SETTINGS_KEY = 'email';
/** Réglages de visibilité des agendas. */
export const CALENDAR_SETTINGS_KEY = 'calendar';
/** Nombre maximal d'agences déclarées par société. */
export const TENANT_AGENCY_MAX = 20;
/** Lignes d'adresse d'une agence, dans l'ordre d'affichage postal. */
export function agencyAddressLines(agency) {
    const lines = [];
    if (agency.addressLine1 !== null) {
        lines.push(agency.addressLine1);
    }
    if (agency.addressLine2 !== null) {
        lines.push(agency.addressLine2);
    }
    const cityLine = [agency.postalCode, agency.city].filter((part) => part !== null).join(' ');
    if (cityLine.length > 0) {
        lines.push(cityLine);
    }
    return lines;
}
/** Adresse d'une agence sur une seule ligne, ou `null` si aucune adresse n'est renseignée. */
export function formatAgencyAddress(agency) {
    const lines = agencyAddressLines(agency);
    return lines.length === 0 ? null : lines.join(', ');
}
/** Agences déclarées correspondant aux identifiants fournis, dans l'ordre des identifiants. */
export function selectAgencies(agencies, ids) {
    const wanted = new Set(ids);
    return agencies.filter((agency) => wanted.has(agency.id));
}
/**
 * Décode la liste d'identifiants d'agences stockée en JSON par une adhésion.
 * Une valeur illisible est traitée comme une absence de rattachement, jamais comme une erreur.
 */
export function parseStoredAgencyIds(raw) {
    if (raw === null || raw === undefined || raw.trim() === '') {
        return [];
    }
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed)
            ? [...new Set(parsed.filter((value) => typeof value === 'string'))].slice(0, TENANT_AGENCY_MAX)
            : [];
    }
    catch {
        return [];
    }
}
/** Nombre maximal d'entrées déclarées dans un référentiel CRM. */
export const TENANT_CRM_DIRECTORY_MAX = 50;
/** Civilités proposées par défaut dans les fiches CRM. */
export const DEFAULT_CRM_CIVILITIES = [
    'Mr',
    'Monsieur',
    'Madame',
    'Mme',
    'Mademoiselle',
    'Melle',
    'Societe',
    'Association',
    'Club',
];
/** Référentiel des sources : vide par défaut, il se nourrit des réglages et des valeurs saisies. */
export const DEFAULT_CRM_SOURCES = [];
/** Réglages d'identité opérationnelle par défaut. */
export const DEFAULT_COMPANY_SETTINGS = {
    insuranceNumber: null,
    insuranceProvider: null,
    insuranceAddress: null,
    insuranceExpiresAt: null,
    activityProfiles: [],
    agencies: [],
    logo: null,
    insuranceDocument: null,
    crmCivilities: [...DEFAULT_CRM_CIVILITIES],
    crmSources: [...DEFAULT_CRM_SOURCES],
};
function asPlainObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value
        : null;
}
function asTrimmedString(value) {
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}
function resolveStoredDocumentMetadata(value) {
    const raw = asPlainObject(value);
    const filename = asTrimmedString(raw?.filename);
    const mimeType = asTrimmedString(raw?.mimeType);
    const updatedAt = asTrimmedString(raw?.updatedAt);
    const storedPath = asTrimmedString(raw?.path);
    if (filename === null || mimeType === null || updatedAt === null || storedPath === null) {
        return null;
    }
    return { filename, mimeType, updatedAt, path: storedPath };
}
function resolveCrmDirectoryEntries(value, fallback) {
    if (!Array.isArray(value)) {
        return [...fallback];
    }
    const entries = value
        .map(asTrimmedString)
        .filter((entry) => entry !== null)
        .slice(0, TENANT_CRM_DIRECTORY_MAX);
    return entries.length > 0
        ? [...new Set(entries)].sort((left, right) => left.localeCompare(right, 'fr'))
        : [...fallback];
}
/** Résout une agence à partir d'une entrée inconnue des paramètres avancés. */
function resolveAgency(value) {
    const raw = asPlainObject(value);
    const id = asTrimmedString(raw?.id);
    const label = asTrimmedString(raw?.label);
    if (id === null || label === null) {
        return null;
    }
    const countryCode = asTrimmedString(raw?.countryCode);
    return {
        id,
        label,
        addressLine1: asTrimmedString(raw?.addressLine1),
        addressLine2: asTrimmedString(raw?.addressLine2),
        postalCode: asTrimmedString(raw?.postalCode),
        city: asTrimmedString(raw?.city),
        countryCode: countryCode === null ? 'FR' : countryCode.toUpperCase(),
        phone: asTrimmedString(raw?.phone),
        email: asTrimmedString(raw?.email),
        siret: asTrimmedString(raw?.siret),
    };
}
/** Résout l'identité opérationnelle à partir des paramètres avancés, avec valeurs par défaut. */
export function resolveCompanySettings(settings) {
    const raw = asPlainObject(settings?.[COMPANY_SETTINGS_KEY]);
    if (raw === null) {
        return { ...DEFAULT_COMPANY_SETTINGS, activityProfiles: [], agencies: [] };
    }
    const activityProfiles = Array.isArray(raw.activityProfiles)
        ? raw.activityProfiles.filter(isCompanyActivityProfile)
        : [];
    const agencies = Array.isArray(raw.agencies)
        ? raw.agencies
            .map(resolveAgency)
            .filter((agency) => agency !== null)
            .slice(0, TENANT_AGENCY_MAX)
        : [];
    return {
        insuranceNumber: asTrimmedString(raw.insuranceNumber),
        insuranceProvider: asTrimmedString(raw.insuranceProvider),
        insuranceAddress: asTrimmedString(raw.insuranceAddress),
        insuranceExpiresAt: asTrimmedString(raw.insuranceExpiresAt),
        activityProfiles: [...new Set(activityProfiles)],
        agencies,
        logo: resolveStoredDocumentMetadata(raw.logo),
        insuranceDocument: resolveStoredDocumentMetadata(raw.insuranceDocument),
        crmCivilities: resolveCrmDirectoryEntries(raw.crmCivilities, DEFAULT_CRM_CIVILITIES),
        crmSources: resolveCrmDirectoryEntries(raw.crmSources, DEFAULT_CRM_SOURCES),
    };
}
// ---------------------------------------------------------------------------------------------
// Validité de l'attestation d'assurance
// ---------------------------------------------------------------------------------------------
/** Nombre de jours avant échéance à partir duquel l'attestation est signalée à renouveler. */
export const INSURANCE_EXPIRY_WARNING_DAYS = 30;
/**
 * État de validité de l'attestation à une date de référence (aujourd'hui par défaut).
 * `missing` couvre l'absence de date comme une date illisible.
 */
export function resolveInsuranceValidity(expiresAt, reference = new Date()) {
    if (expiresAt === null) {
        return 'missing';
    }
    const expiry = new Date(`${expiresAt}T23:59:59`);
    if (Number.isNaN(expiry.getTime())) {
        return 'missing';
    }
    const today = new Date(reference.getFullYear(), reference.getMonth(), reference.getDate());
    if (expiry.getTime() < today.getTime()) {
        return 'expired';
    }
    const warningThreshold = new Date(today);
    warningThreshold.setDate(warningThreshold.getDate() + INSURANCE_EXPIRY_WARNING_DAYS);
    return expiry.getTime() <= warningThreshold.getTime() ? 'expiring' : 'valid';
}
/** Nombre maximal de modèles de confirmation de rendez-vous déclarés par société. */
export const TENANT_APPOINTMENT_TEMPLATE_MAX = 30;
/** Réglages d'e-mail par défaut : signature désactivée tant qu'elle n'est pas configurée. */
export const DEFAULT_EMAIL_SETTINGS = {
    signature: {
        enabled: false,
        senderName: null,
        includeLogo: true,
        text: null,
    },
    templates: {},
    appointmentTemplates: [],
};
/** Résout les gabarits personnalisés déclarés dans `settings.email.templates`. */
function resolveEmailTemplates(raw) {
    if (raw === null) {
        return {};
    }
    const templates = {};
    for (const kind of MAIL_TEMPLATE_KINDS) {
        const entry = asPlainObject(raw[kind]);
        if (entry === null) {
            continue;
        }
        templates[kind] = {
            subject: asTrimmedString(entry.subject),
            body: asTrimmedString(entry.body),
        };
    }
    return templates;
}
/** Résout un modèle nommé de confirmation de rendez-vous, ou `null` si l'entrée est inexploitable. */
function resolveAppointmentTemplate(value) {
    const raw = asPlainObject(value);
    const id = asTrimmedString(raw?.id);
    const name = asTrimmedString(raw?.name);
    if (id === null || name === null) {
        return null;
    }
    return { id, name, body: asTrimmedString(raw?.body) };
}
/** Résout les modèles nommés de confirmation de rendez-vous déclarés dans les paramètres. */
function resolveAppointmentTemplates(raw) {
    if (!Array.isArray(raw)) {
        return [];
    }
    return raw
        .map(resolveAppointmentTemplate)
        .filter((template) => template !== null)
        .slice(0, TENANT_APPOINTMENT_TEMPLATE_MAX);
}
/** Résout les réglages d'e-mail à partir des paramètres avancés, avec valeurs par défaut. */
export function resolveEmailSettings(settings) {
    const raw = asPlainObject(settings?.[EMAIL_SETTINGS_KEY]);
    const signature = asPlainObject(raw?.signature);
    const templates = resolveEmailTemplates(asPlainObject(raw?.templates));
    const appointmentTemplates = resolveAppointmentTemplates(raw?.appointmentTemplates);
    if (signature === null) {
        return {
            signature: { ...DEFAULT_EMAIL_SETTINGS.signature },
            templates,
            appointmentTemplates,
        };
    }
    return {
        signature: {
            enabled: signature.enabled === true,
            senderName: asTrimmedString(signature.senderName),
            includeLogo: signature.includeLogo !== false,
            text: asTrimmedString(signature.text),
        },
        templates,
        appointmentTemplates,
    };
}
// ---------------------------------------------------------------------------------------------
// Agenda : visibilité et horaires de travail
// ---------------------------------------------------------------------------------------------
/** Modes de visibilité des agendas de l'équipe. */
export const CALENDAR_VISIBILITY_MODES = ['team', 'restricted'];
/** Mode par défaut : toute l'équipe voit les agendas de l'équipe. */
export const DEFAULT_CALENDAR_VISIBILITY = 'team';
export const CALENDAR_VISIBILITY_MODE_LABELS = {
    team: "Toute l'équipe",
    restricted: 'Profils autorisés uniquement',
};
export const CALENDAR_VISIBILITY_MODE_DESCRIPTIONS = {
    team: "Chaque membre voit les rendez-vous de toute l'équipe.",
    restricted: "Seuls les rôles autorisés voient les agendas de toute l'équipe ; les autres ne voient que leurs propres rendez-vous et ceux auxquels ils participent.",
};
/** Indique si une valeur inconnue est un mode de visibilité valide. */
export function isCalendarVisibilityMode(value) {
    return CALENDAR_VISIBILITY_MODES.includes(value);
}
/** Nombre maximal de plages d'ouverture déclarées pour une journée. */
export const TENANT_WORKING_HOURS_MAX = 4;
/** Une heure locale au format `HH:MM`. */
const WORKING_HOUR_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
/**
 * Plages d'ouverture proposées par défaut : matinée puis après-midi, coupure méridienne incluse.
 * Elles servent également de repli lorsque la société n'a rien enregistré.
 */
export const DEFAULT_WORKING_HOURS = [
    { start: '08:00', end: '12:00' },
    { start: '13:30', end: '17:00' },
];
/** Indique si une valeur inconnue est une heure locale valide au format `HH:MM`. */
export function isWorkingHour(value) {
    return typeof value === 'string' && WORKING_HOUR_PATTERN.test(value);
}
/** Résout une plage d'ouverture ; `null` si les bornes sont illisibles ou incohérentes. */
export function resolveWorkingHoursRange(value) {
    const raw = asPlainObject(value);
    const start = raw?.start;
    const end = raw?.end;
    if (!isWorkingHour(start) || !isWorkingHour(end) || start >= end) {
        return null;
    }
    return { start, end };
}
/** Copies mutables des plages par défaut (les constantes exportées restent figées). */
function defaultWorkingHours() {
    return DEFAULT_WORKING_HOURS.map((range) => ({ ...range }));
}
/** Résout les plages d'ouverture déclarées ; repli sur les plages par défaut si aucune n'est lisible. */
export function resolveWorkingHours(value) {
    if (!Array.isArray(value)) {
        return defaultWorkingHours();
    }
    const ranges = value
        .map(resolveWorkingHoursRange)
        .filter((range) => range !== null)
        .sort((left, right) => left.start.localeCompare(right.start))
        .slice(0, TENANT_WORKING_HOURS_MAX);
    return ranges.length > 0 ? ranges : defaultWorkingHours();
}
/** Bornes de la journée de travail, ou `null` si aucune plage exploitable n'est déclarée. */
export function workingHoursBounds(hours) {
    let start = null;
    let end = null;
    for (const range of hours) {
        if (!isWorkingHour(range.start) || !isWorkingHour(range.end) || range.start >= range.end) {
            continue;
        }
        if (start === null || range.start < start) {
            start = range.start;
        }
        if (end === null || range.end > end) {
            end = range.end;
        }
    }
    return start === null || end === null ? null : { start, end };
}
/** Réglages d'agenda par défaut. */
export const DEFAULT_CALENDAR_SETTINGS = {
    visibility: DEFAULT_CALENDAR_VISIBILITY,
    teamRoleCodes: [],
    workingHours: DEFAULT_WORKING_HOURS.map((range) => ({ ...range })),
};
/** Résout les réglages d'agenda à partir des paramètres avancés, avec valeurs par défaut. */
export function resolveCalendarSettings(settings) {
    const raw = asPlainObject(settings?.[CALENDAR_SETTINGS_KEY]);
    if (raw === null) {
        return {
            visibility: DEFAULT_CALENDAR_VISIBILITY,
            teamRoleCodes: [],
            workingHours: defaultWorkingHours(),
        };
    }
    const teamRoleCodes = Array.isArray(raw.teamRoleCodes)
        ? raw.teamRoleCodes.filter((code) => typeof code === 'string')
        : [];
    return {
        visibility: isCalendarVisibilityMode(raw.visibility)
            ? raw.visibility
            : DEFAULT_CALENDAR_VISIBILITY,
        teamRoleCodes: [...new Set(teamRoleCodes)],
        workingHours: resolveWorkingHours(raw.workingHours),
    };
}
//# sourceMappingURL=company.constants.js.map